"use client";

import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useState } from "react";

export interface SearchableStoreOption {
  _id: string;
  name: string;
}

interface SearchableStoreSelectProps {
  stores: SearchableStoreOption[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  showAllOption?: boolean;
  allLabel?: string;
  disabled?: boolean;
}

export default function SearchableStoreSelect({
  stores,
  value,
  onValueChange,
  placeholder = "Select store",
  searchPlaceholder = "Search stores...",
  showAllOption = false,
  allLabel = "All stores",
  disabled = false,
}: SearchableStoreSelectProps) {
  const [open, setOpen] = useState(false);
  const selectedStore = stores.find((store) => store._id === value);

  const selectValue = (nextValue: string) => {
    onValueChange(nextValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedStore?.name || (showAllOption && !value ? allLabel : placeholder)}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No stores found.</CommandEmpty>
            <CommandGroup>
              {showAllOption && (
                <CommandItem value={`all ${allLabel}`} onSelect={() => selectValue("")}>
                  <Check className={cn("mr-2 h-4 w-4", !value ? "opacity-100" : "opacity-0")} />
                  {allLabel}
                </CommandItem>
              )}
              {stores.map((store) => (
                <CommandItem
                  key={store._id}
                  value={`${store.name} ${store._id}`}
                  onSelect={() => selectValue(store._id)}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === store._id ? "opacity-100" : "opacity-0")} />
                  {store.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}